import React, { useMemo, useState } from 'react';
import { Flex, List, message, Switch } from 'antd';
import styles from '../../assets/styles/CutomStyle/ShareLinkDisplay.module.css';
import listStyles from './BulkShareTrackingLinks.module.css';
import CustomParagraph from '../../components/CustomParagraph';
import CustomInput from '../../components/CustomInput';
import CustomButton from '../../components/CustomButton';
import { useSpin } from '../../components/SpinContext';
import { ShareShipmentApi } from '../../apis/shipment';
import theme from '../../assets/theme';
import {
  BulkShareShipment,
  buildShareLink,
  formatTrackingLinkLines,
  mapWithConcurrency,
} from './bulkShareTrackingLinks.utils';

interface BulkShareTrackingLinksProps {
  shipments: BulkShareShipment[];
  token: string;
  /** How many shipments the toolbar counts as selected, when that is more than are listed. */
  selectedCount?: number;
}

type OptionKey =
  | 'showProductDetails'
  | 'showAlerts'
  | 'showSensorData'
  | 'maskTrackingLink'
  | 'requirePassword';

type Options = Record<OptionKey, boolean>;

const OPTION_LABELS: { key: OptionKey; name: string }[] = [
  { key: 'showProductDetails', name: 'Show product details' },
  { key: 'showAlerts', name: 'Show alerts' },
  { key: 'showSensorData', name: 'Show sensor data' },
  { key: 'maskTrackingLink', name: 'Mask tracking number' },
  { key: 'requirePassword', name: 'Require password' },
];

/** Same defaults as the single-shipment share. */
const DEFAULT_OPTIONS: Options = {
  showProductDetails: false,
  showAlerts: false,
  showSensorData: true,
  maskTrackingLink: false,
  requirePassword: false,
};

/** Options that only work on password-protected links. */
const isPasswordGated = (key: OptionKey): boolean =>
  key === 'showProductDetails' || key === 'showAlerts';

const SHARE_CONCURRENCY = 5;

const newTokens = (shipments: BulkShareShipment[]): Record<string, string> =>
  Object.fromEntries(shipments.map((s) => [s.id, crypto.randomUUID()]));

const fallbackCopyToClipboard = (text: string): boolean => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);
  return copied;
};

const copyText = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return fallbackCopyToClipboard(text);
    }
  }
  return fallbackCopyToClipboard(text);
};

const plural = (count: number, word: string): string =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Creates one share link per selected shipment, with the same settings and
 * endpoint as the single-shipment share, and copies them all at once as
 * `[Reference ID]: [link]` lines.
 */
const BulkShareTrackingLinks: React.FC<BulkShareTrackingLinksProps> = ({
  shipments,
  token,
  selectedCount,
}) => {
  const baseUrl = theme.shareUrl;
  const { showSpin, hideSpin } = useSpin();

  const [options, setOptions] = useState<Options>(DEFAULT_OPTIONS);
  const [password, setPassword] = useState('');
  // Each token is single-use on the backend. After an attempt the listed links
  // stay the ones that were created and copied; fresh tokens are only drawn
  // when the next attempt starts, so the list never shows an uncreated link.
  const [tokens, setTokens] = useState(() => newTokens(shipments));
  const [tokensSpent, setTokensSpent] = useState(false);
  const [failedIds, setFailedIds] = useState<string[]>([]);
  const [manualCopyText, setManualCopyText] = useState<string | null>(null);

  const links = useMemo(
    () =>
      shipments.map((shipment) => ({
        shipment,
        link: buildShareLink(
          baseUrl,
          shipment.id,
          tokens[shipment.id] ?? crypto.randomUUID()
        ),
      })),
    [baseUrl, shipments, tokens]
  );

  const toggleOption = (key: OptionKey): void => {
    const next = { ...options, [key]: !options[key] };
    if (key === 'requirePassword') {
      setPassword('');
      if (!next.requirePassword) {
        next.showProductDetails = false;
        next.showAlerts = false;
      }
    }
    setOptions(next);
  };

  const shareOne = async (
    shipment: BulkShareShipment,
    attemptTokens: Record<string, string>
  ): Promise<{ shipment: BulkShareShipment; ok: boolean }> => {
    try {
      const res = await ShareShipmentApi(token, {
        shipment: shipment.id,
        show_product_details: options.showProductDetails,
        show_alerts: options.showAlerts,
        show_sensor_data: options.showSensorData,
        hidden_vitals: [],
        mask_tracking_link: options.maskTrackingLink,
        require_password: options.requirePassword,
        password: options.requirePassword ? password : null,
        token: attemptTokens[shipment.id],
      });
      const data = res?.data as { data?: { is_success?: boolean } } | undefined;
      return { shipment, ok: Boolean(data?.data?.is_success) };
    } catch {
      return { shipment, ok: false };
    }
  };

  const handleCopyLinks = async (): Promise<void> => {
    if (options.requirePassword) {
      if (!password) {
        message.error('Please enter a password before copying the links.');
        return;
      }
      if (password.length < 6) {
        message.error('Password must be at least 6 characters long.');
        return;
      }
    }

    // Tokens are unique on the backend and some may already be stored, even by
    // a request that looked failed, so every attempt after the first needs new ones.
    const attemptTokens = tokensSpent ? newTokens(shipments) : tokens;
    setTokens(attemptTokens);
    setTokensSpent(true);

    showSpin();
    let results: { shipment: BulkShareShipment; ok: boolean }[];
    try {
      results = await mapWithConcurrency(shipments, SHARE_CONCURRENCY, (s) =>
        shareOne(s, attemptTokens)
      );
    } finally {
      hideSpin();
    }

    const failed = results.filter((r) => !r.ok).map((r) => r.shipment.id);
    setFailedIds(failed);

    const shared = shipments
      .filter((shipment) => !failed.includes(shipment.id))
      .map((shipment) => ({
        shipment,
        link: buildShareLink(baseUrl, shipment.id, attemptTokens[shipment.id]),
      }));
    if (shared.length === 0) {
      message.error('Could not create tracking links for these shipments.');
      return;
    }

    const text = formatTrackingLinkLines(
      shared.map(({ shipment, link }) => ({
        referenceNumber: shipment.reference_number,
        link,
      }))
    );

    if (await copyText(text)) {
      message.success(`${plural(shared.length, 'tracking link')} copied`);
      if (failed.length > 0) {
        message.warning(
          `${plural(failed.length, 'shipment')} without a tracking link left out`
        );
      }
      setManualCopyText(null);
    } else {
      // The links are already stored, so they are shown for copying by hand.
      message.error('Copy failed. Please copy manually.');
      setManualCopyText(text);
    }
  };

  const hiddenCount =
    selectedCount !== undefined ? selectedCount - shipments.length : 0;

  return (
    <>
      <List
        className='custom-list-container'
        itemLayout='horizontal'
        dataSource={OPTION_LABELS}
        renderItem={(item) => (
          <List.Item className='custom-radio-list-item'>
            <CustomParagraph
              text={item.name}
              fontSize='14px'
              fontWeight={400}
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='0px'
            />
            <Switch
              checked={options[item.key]}
              disabled={isPasswordGated(item.key) && !options.requirePassword}
              onChange={() => toggleOption(item.key)}
              style={{ marginLeft: '10px' }}
              aria-label={item.name}
            />
          </List.Item>
        )}
        style={{ margin: '0 0 12px' }}
      />

      {options.requirePassword && (
        <Flex vertical style={{ marginBottom: '20px' }}>
          <CustomInput
            placeholder="Set password you'll share to open the tracking links"
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            height='36px'
            lineHeight='20px'
          />
        </Flex>
      )}

      <CustomParagraph
        text={`${plural(shipments.length, 'shipment')}`}
        fontSize='12px'
        fontWeight={600}
        lineHeight='16px'
        color='var(--text-secondary)'
        marginBottom='8px'
      />
      <ul aria-label='Shipments to share' className={listStyles.list}>
        {links.map(({ shipment, link }) => {
          const isFailed = failedIds.includes(shipment.id);
          return (
            <li key={shipment.id} className={listStyles.row}>
              <span className={listStyles.reference}>
                {shipment.reference_number?.trim() || '-'}
              </span>
              {isFailed ? (
                <span className={listStyles.failed}>
                  No tracking link available. Not copied.
                </span>
              ) : (
                <span title={link} className={listStyles.link}>
                  {link}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {hiddenCount > 0 && (
        <CustomParagraph
          text={`Only the ${plural(shipments.length, 'loaded shipment')} are listed. Scroll the list to load the other ${hiddenCount} selected.`}
          fontSize='12px'
          fontWeight={400}
          lineHeight='16px'
          color='var(--text-tertiary)'
          marginBottom='12px'
        />
      )}

      {manualCopyText !== null && (
        <textarea
          aria-label='Tracking links'
          readOnly
          value={manualCopyText}
          onFocus={(e) => e.currentTarget.select()}
          rows={Math.min(6, manualCopyText.split('\n').length)}
          className={listStyles.manualCopy}
        />
      )}

      <Flex justify='space-between' gap={16} align='center'>
        <div className={styles.wrap}>
          <div
            className={styles.box}
            title={links.map((l) => l.link).join(', ')}
          >
            <span className={styles.text}>
              {links.map((l) => l.link).join(', ')}
            </span>
            <span className={styles.gutter} aria-hidden='true' />
          </div>
        </div>

        <CustomButton
          onClick={handleCopyLinks}
          text='Copy links'
          height='36px'
          width='95px'
          fontSize='14px'
          fontWeight='500'
          color='#fff'
          bgColor='#000'
          disabled={shipments.length === 0}
        />
      </Flex>
    </>
  );
};

export default BulkShareTrackingLinks;
